import { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { AuthContext } from "../context/Auth/AuthContext";
import api from "../services/api";
import { normalizeItems, applyHealthEvent, dismissKey, visibleAlerts } from "../helpers/aiProviderHealthState";

const POLL_MS = 5 * 60 * 1000;

const safeStorage = () => {
  try {
    return window.sessionStorage;
  } catch (e) {
    return { getItem: () => null, setItem: () => {} };
  }
};

/**
 * Alertas "provedor de IA sem crédito" para admins. GET no mount + socket
 * `company-{id}-ai-provider-health` + poll de segurança a cada 5 min.
 */
const useAIProviderHealth = () => {
  const { user, socket } = useContext(AuthContext);
  const isAdmin = Boolean(user && (user.profile === "admin" || user.super));
  const companyId = user?.companyId;

  const [alerts, setAlerts] = useState([]);
  const [blockSeconds, setBlockSeconds] = useState(300);
  const [gateEnabled, setGateEnabled] = useState(true);
  const [dismissTick, setDismissTick] = useState(0);

  const alertsRef = useRef([]);
  const mountedRef = useRef(true);
  const companyRef = useRef(companyId);

  useEffect(() => {
    alertsRef.current = alerts;
  }, [alerts]);

  useEffect(() => {
    companyRef.current = companyId;
  }, [companyId]);

  const refresh = useCallback(async () => {
    if (!isAdmin || !companyId) return;
    try {
      const { data } = await api.get("/ai-analytics/provider-health");
      if (!mountedRef.current || companyRef.current !== companyId) return;
      setAlerts(normalizeItems(data?.items));
      if (Number.isFinite(Number(data?.blockSeconds))) setBlockSeconds(Number(data.blockSeconds));
      if (typeof data?.gateEnabled === "boolean") setGateEnabled(data.gateEnabled);
    } catch (e) {
      // silencioso: banner é informativo
    }
  }, [isAdmin, companyId]);

  useEffect(() => {
    if (!isAdmin || !companyId) return undefined;
    mountedRef.current = true;
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
  }, [isAdmin, companyId, refresh]);

  useEffect(() => {
    if (!isAdmin || !companyId || !socket) return undefined;
    const eventName = `company-${companyId}-ai-provider-health`;
    const handler = (event) => {
      const isNewProvider =
        event?.status === "billing" && !alertsRef.current.some((a) => a.provider === event.provider);
      const next = applyHealthEvent(alertsRef.current, event);
      alertsRef.current = next;
      setAlerts(next);
      if (isNewProvider) refresh();
    };
    socket.on(eventName, handler);
    return () => socket.off(eventName, handler);
  }, [isAdmin, companyId, socket, refresh]);

  const dismiss = useCallback(
    (provider) => {
      const target = alerts.find((a) => a.provider === provider);
      if (!target || !companyId) return;
      try {
        safeStorage().setItem(dismissKey(companyId, target), "1");
      } catch (e) {
        // ignore
      }
      setDismissTick((t) => t + 1);
    },
    [alerts, companyId]
  );

  const visible = useMemo(
    () => (companyId ? visibleAlerts(alerts, safeStorage(), companyId) : []),
    // dismissTick força recomputar após dismiss
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [alerts, companyId, dismissTick]
  );

  return { alerts, visible, dismiss, refresh, blockSeconds, gateEnabled, isAdmin };
};

export default useAIProviderHealth;
